import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { getPageFromSlug, generateLocalizedUrl } from "@/lib/slugUtils";
import { type SupportedLanguage } from "@/lib/i18n";

interface BreadcrumbProps {
  className?: string;
}

const Breadcrumb = ({ className = "" }: BreadcrumbProps) => {
  const { t } = useTranslation(['common']);
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLanguage = (lang || 'en') as SupportedLanguage;
  
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isHomePage = pathSegments.length === 1; // Only language segment
  
  if (isHomePage) return null;
  
  const currentSlug = pathSegments[1];
  const currentPage = getPageFromSlug(currentSlug, currentLanguage);
  
  if (!currentPage) return null;
  
  const breadcrumbItems = [
    {
      name: t('navigation.home'),
      url: `/${currentLanguage}/`,
      position: 1
    },
    {
      name: t(`navigation.${currentPage}`),
      url: generateLocalizedUrl(currentPage, currentLanguage),
      position: 2
    }
  ];

  // Structured data for breadcrumbs
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map(item => ({
      "@type": "ListItem",
      "position": item.position,
      "name": item.name,
      "item": `https://www.priceforms.com${item.url}`
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav className={`flex items-center space-x-2 text-sm text-muted-foreground ${className}`} aria-label="Breadcrumb">
        <Link 
          to={`/${currentLanguage}/`}
          className="flex items-center hover:text-foreground transition-colors"
          aria-label={t('navigation.home')}
        >
          <Home className="w-4 h-4" />
          <span className="sr-only">{t('navigation.home')}</span>
        </Link>
        
        <ChevronRight className="w-4 h-4" />
        
        <span className="text-foreground font-medium" aria-current="page">
          {t(`navigation.${currentPage}`)}
        </span>
      </nav>
    </>
  );
};

export default Breadcrumb;